import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

import { apiUrl } from "../api/endpoints";
import { useAuth } from "../hooks/AuthProvider";
import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import { getErrorMessage } from "../utils/errorUtils";
import { LanguageContext } from "../context/LanguageContext";
import plansExtraTranslations from "../languages/plansExtra.json";
import "./DevicePlanDetails.css";

const formatValue = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") {
    try { return JSON.stringify(value); } catch (_e) { return String(value); }
  }
  return String(value);
};

const formatDate = (value, locale, fallback) => {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : d.toLocaleString(locale);
};

export default function DevicePlanDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const { user } = useAuth();
  const rootT = plansExtraTranslations[lang]?.devicePlanLifecycle || plansExtraTranslations.en.devicePlanLifecycle;
  const t = rootT.details;
  const isSuperUser = String(user?.role || "").toLowerCase() === "super";
  const locale = lang === "ar" ? "ar-AE" : lang === "ur" ? "ur-PK" : "en-GB";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertState, setAlertState] = useState({ open: false, message: "", type: "error" });

  const load = useCallback(async () => {
    if (!isSuperUser) return;
    setLoading(true);
    try {
      const res = await axios.get(apiUrl(`device/plan-details/${id}`));
      setData(res?.data?.data || null);
    } catch (err) {
      setData(null);
      setAlertState({ open: true, message: getErrorMessage(err) || t.loadFailed, type: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, isSuperUser, t.loadFailed]);

  useEffect(() => { load(); }, [load]);

  const statusLabel = useMemo(() => {
    const status = String(data?.plan_status || "").toLowerCase();
    if (status === "pending_activation") return rootT.statuses.pendingActivation;
    if (status === "expired") return rootT.statuses.expired;
    if (status === "disabled") return rootT.statuses.disabled;
    return rootT.statuses.active;
  }, [data?.plan_status, rootT.statuses]);

  const sections = useMemo(() => {
    if (!data) return [];
    return [
      {
        key: "plan", title: t.sections.plan, icon: EventOutlinedIcon,
        items: [
          [t.fields.status, statusLabel],
          [t.fields.duration, data.duration_plan],
          [t.fields.expiredAt, formatDate(data.expired_at, locale, t.noValue)],
          [t.fields.daysToExpire, data.days_to_expire],
          [t.fields.requestNote, data.plan_request_note],
        ],
      },
      {
        key: "device", title: t.sections.device, icon: DevicesOtherOutlinedIcon,
        items: [
          [t.fields.deviceId, data.device_id ?? data.id],
          [t.fields.subId, data.subid],
          [t.fields.did, data.did],
          [t.fields.name, data.name],
          [t.fields.created, formatDate(data.created, locale, t.noValue)],
          [t.fields.firmwareUpdated, formatDate(data.firmware_updated_on, locale, t.noValue)],
          [t.fields.tags, data.tags],
          [t.fields.icon, data.icon],
        ],
      },
      {
        key: "ownership", title: t.sections.ownership, icon: BusinessOutlinedIcon,
        items: [
          [t.fields.subscriber, data.subscriber?.name],
          [t.fields.subscriberEmail, data.subscriber?.email],
          [t.fields.organization, data.organization?.name],
          [t.fields.organizationId, data.organization_id],
          [t.fields.template, data.template?.name],
          [t.fields.templateId, data.template_id],
          [t.fields.owner, data.owner?.name],
          [t.fields.ownerId, data.owner_id],
          [t.fields.location, data.location?.name],
          [t.fields.locationId, data.location_id],
        ],
      },
      {
        key: "request", title: t.sections.request, icon: PersonOutlineOutlinedIcon,
        items: [
          [t.fields.requestedBy, data.requestedBy?.name],
          [t.fields.requestedByEmail, data.requestedBy?.email],
          [t.fields.lastChangedBy, data.lastPlanChangedBy?.name],
          [t.fields.lastChangedByEmail, data.lastPlanChangedBy?.email],
        ],
      },
    ];
  }, [data, locale, statusLabel, t]);

  if (!isSuperUser) {
    return <div className="device-plan-details-page"><div className="device-plan-details-empty">{rootT.noPermission}</div></div>;
  }

  return (
    <div className="device-plan-details-page">
      <Alert open={alertState.open} close={() => setAlertState((s) => ({ ...s, open: false }))} message={alertState.message} type={alertState.type} position={["ar", "ur"].includes(lang) ? "left" : "right"} />

      <header className="device-plan-details-header">
        <div className="device-plan-details-heading">
          <button type="button" className="device-plan-details-icon-btn" onClick={() => navigate("/administration/device-plans")} aria-label={t.back} title={t.back}>
            <ArrowBackIosNewOutlinedIcon />
          </button>
          <div>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>
        </div>
        <div className="device-plan-details-actions">
          <button type="button" className="devices-secondary-btn" onClick={load} disabled={loading}>
            <RefreshIcon fontSize="small" /><span>{t.refresh}</span>
          </button>
          <button
            type="button"
            className="devices-add-btn"
            disabled={!data?.device_id && !data?.id}
            onClick={() => navigate(`/devices/details/${data?.device_id || data?.id}`)}
          >
            <OpenInNewOutlinedIcon fontSize="small" /><span>{t.openDevice}</span>
          </button>
        </div>
      </header>

      {loading ? <div className="device-plan-details-loading"><Spinner /></div> : !data ? (
        <div className="device-plan-details-empty">{t.notFound}</div>
      ) : (
        <div className="device-plan-details-content">
          <div className="device-plan-details-hero">
            <div className="device-plan-details-hero-icon"><DevicesOtherOutlinedIcon /></div>
            <div className="device-plan-details-hero-main">
              <span>{t.fields.name}</span>
              <strong>{formatValue(data.name, t.noValue)}</strong>
              <small>{t.fields.deviceId}: {formatValue(data.device_id ?? data.id, t.noValue)} · {t.fields.did}: {formatValue(data.did, t.noValue)}</small>
            </div>
            <div className={`device-plan-status-badge device-plan-status-${String(data.plan_status || "unknown").replace(/_/g, "-")}`}>{statusLabel}</div>
          </div>

          <div className="device-plan-details-grid">
            {sections.map(({ key, title, icon: Icon, items }) => (
              <section className="device-plan-details-card" key={key}>
                <div className="device-plan-details-card-title"><Icon /><span>{title}</span></div>
                <div className="device-plan-details-fields">
                  {items.map(([label, value]) => (
                    <div className="device-plan-details-field" key={label}>
                      <span>{label}</span>
                      <strong title={formatValue(value, t.noValue)}>{formatValue(value, t.noValue)}</strong>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <section className="device-plan-details-card device-plan-details-notifications">
            <div className="device-plan-details-card-title"><HistoryOutlinedIcon /><span>{t.sections.notifications}</span></div>
            {Array.isArray(data.plan_notifications) && data.plan_notifications.length ? (
              <div className="device-plan-notification-list">
                {data.plan_notifications.map((item) => (
                  <article className="device-plan-notification-item" key={item.id}>
                    <div className="device-plan-notification-head">
                      <strong>{formatValue(item.title, t.noValue)}</strong>
                      <span>{formatDate(item.created_at, locale, t.noValue)}</span>
                    </div>
                    <p>{formatValue(item.message, t.noValue)}</p>
                    <div className="device-plan-notification-meta">
                      <span>{t.notificationType}: {formatValue(item.type, t.noValue)}</span>
                      <span>{t.notificationStatus}: {formatValue(item.status, t.noValue)}</span>
                    </div>
                  </article>
                ))}
              </div>
            ) : <div className="device-plan-details-no-notifications">{t.noNotifications}</div>}
          </section>
        </div>
      )}
    </div>
  );
}
